import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import {
  getContractWithDetails,
  acceptContract,
  startContract,
  submitContractForReview,
  cancelContract,
  disputeContract,
  submitMilestone,
  createTimeEntry,
} from '../../../services/contractService';

// ── Static maps ───────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  offered:        'bg-info/10 text-info border-info/30',
  accepted:       'bg-success/10 text-success border-success/30',
  in_progress:    'bg-primary/10 text-primary border-primary/30',
  pending_review: 'bg-warning/10 text-warning border-warning/30',
  completed:      'bg-success/10 text-success border-success/30',
  cancelled:      'bg-destructive/10 text-destructive border-destructive/30',
  disputed:       'bg-warning/10 text-warning border-warning/30',
};
const STATUS_LABELS = {
  offered: 'New Offer', accepted: 'Accepted', in_progress: 'In Progress',
  pending_review: 'Pending Review', completed: 'Completed', cancelled: 'Cancelled', disputed: 'Disputed',
};
const MILESTONE_CHIP = {
  pending:     'bg-muted text-muted-foreground',
  in_progress: 'bg-primary/10 text-primary',
  submitted:   'bg-warning/10 text-warning',
  approved:    'bg-success/10 text-success',
  rejected:    'bg-destructive/10 text-destructive',
};

// ── Helper sub-components ─────────────────────────────────────────────────────
const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg">
    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size={15} className="text-muted-foreground" />
    </div>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  </div>
);

const PayRow = ({ label, value, isHighlit }) => (
  <div className={`flex items-center justify-between py-2.5 ${isHighlit ? 'border-t border-border mt-1' : ''}`}>
    <p className={`text-sm ${isHighlit ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</p>
    <p className={`text-sm font-bold ${isHighlit ? 'text-primary text-base' : 'text-foreground'}`}>{value}</p>
  </div>
);

const EmptyState = ({ icon, message }) => (
  <div className="text-center py-14">
    <Icon name={icon} className="w-14 h-14 text-muted-foreground mx-auto mb-3 opacity-50" />
    <p className="text-muted-foreground text-sm">{message}</p>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const ContractDetailModal = ({ contract: init, expertId, onClose, onRefresh }) => {
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(null);
  const [entry, setEntry] = useState({ workDate: '', hours: '', description: '' });
  const [submittingEntry, setSubmittingEntry] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showDisputeConfirm, setShowDisputeConfirm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');

  const tabs = [
    { id: 'overview',   label: 'Overview',   icon: 'FileText'     },
    ...(init?.contract_type === 'milestone' ? [{ id: 'milestones', label: 'Milestones', icon: 'CheckSquare' }] : []),
    ...(init?.contract_type === 'hourly'    ? [{ id: 'timesheet',  label: 'Timesheet',  icon: 'Clock'       }] : []),
    { id: 'payments',   label: 'Payments',   icon: 'DollarSign'   },
  ];

  // Always fetch fresh data every time the modal opens (modal remounts on each open).
  useEffect(() => { loadDetails(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const loadDetails = async () => {
    setLoading(true);
    const result = await getContractWithDetails(init?.id);
    if (result?.success) {
      setContract(result.contract);
    } else {
      toast.error('Failed to load contract details');
      setContract(init);
    }
    setLoading(false);
  };

  const handleAction = async (fn, label, args = []) => {
    setActionLoading(label);
    try {
      const result = await fn(...args);
      if (!result?.success) throw new Error(result?.error);
      toast.success(`${label} successful`);
      await loadDetails();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.message || `${label} failed`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelContract = async () => {
    if (!cancelReason.trim()) return toast.error('Please provide a reason for cancellation');
    setActionLoading('Cancel');
    try {
      const hadEscrow = c?.escrow_status === 'held';
      const result = await cancelContract(c.id, cancelReason.trim());
      if (!result?.success) throw new Error(result?.error);
      let successMsg = 'Contract cancelled';
      if (hadEscrow) {
        const earned = result?.expertReceives ?? 0;
        const total  = c?.escrow_amount ?? 0;
        if (earned >= total && total > 0) {
          successMsg = 'Contract cancelled — full escrow released to you';
        } else if (earned > 0) {
          successMsg = `Contract cancelled — you will be paid $${earned.toFixed(2)} for your logged work`;
        } else {
          successMsg = 'Contract cancelled — escrow refunded to client';
        }
      }
      toast.success(successMsg);
      setShowCancelConfirm(false);
      setCancelReason('');
      await loadDetails();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel contract');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitMilestone = async (milestoneId) => {
    setActionLoading(`ms-${milestoneId}`);
    try {
      const result = await submitMilestone(milestoneId);
      if (!result?.success) throw new Error(result?.error);
      toast.success('Milestone submitted for review');
      await loadDetails();
    } catch (err) {
      toast.error(err?.message || 'Failed to submit milestone');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogTime = async (e) => {
    e.preventDefault();
    setSubmittingEntry(true);
    try {
      const result = await createTimeEntry({
        contractId: c.id,
        expertId,
        workDate: entry.workDate,
        hours: parseFloat(entry.hours || 0),
        description: entry.description,
        hourlyRate: c.hourly_rate,
      });
      if (!result?.success) throw new Error(result?.error);
      toast.success('Time entry logged');
      setEntry({ workDate: '', hours: '', description: '' });
      await loadDetails();
    } catch (err) {
      toast.error(err?.message || 'Failed to log time');
    } finally {
      setSubmittingEntry(false);
    }
  };

  if (!init) return null;

  const c = contract || init;
  const milestones   = [...(c.milestones    || [])].sort((a, b) => a.display_order - b.display_order);
  const timeEntries  = [...(c.time_entries  || [])].sort((a, b) => new Date(b.work_date) - new Date(a.work_date));

  // Milestones stats
  const approvedMs   = milestones.filter(m => m.status === 'approved').length;
  const progressPct  = milestones.length > 0 ? Math.round((approvedMs / milestones.length) * 100) : 0;
  const approvedMsValue = milestones.filter(m => m.status === 'approved').reduce((s, m) => s + (m.amount || 0), 0);

  // Timesheet stats
  const totalMins    = timeEntries.reduce((s, e) => s + (e.minutes || 0), 0);
  const totalHours   = (totalMins / 60).toFixed(1);
  const totalBill    = timeEntries.reduce((s, e) => s + (e.billable_amount || 0), 0);
  const approvedBill = timeEntries.filter(e => e.status === 'approved').reduce((s, e) => s + (e.billable_amount || 0), 0);
  const pendingBill  = timeEntries.filter(e => e.status === 'pending').reduce((s, e) => s + (e.billable_amount || 0), 0);

  const canAct = !['completed', 'cancelled', 'disputed'].includes(c.status);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">

        {/* ── Header ── */}
        <div className="flex items-start justify-between p-5 md:p-6 border-b border-border flex-shrink-0">
          <div className="flex-1 min-w-0 mr-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-foreground">{c.title}</h2>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded border flex-shrink-0 ${STATUS_COLORS[c.status] || 'bg-muted text-muted-foreground border-border'}`}>
                {STATUS_LABELS[c.status] || c.status}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              #{c.contract_number} · {c.contract_type === 'hourly' ? 'Hourly' : 'Milestone'} Contract
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              iconName="MessageSquare"
              iconPosition="left"
              onClick={() => navigate(`/expert-dashboard/messages?userId=${c.client_id}`)}
            >
              Message Client
            </Button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex border-b border-border flex-shrink-0 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon} size={15} />
              {tab.label}
              {tab.id === 'milestones' && milestones.length > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-bold">
                  {approvedMs}/{milestones.length}
                </span>
              )}
              {tab.id === 'timesheet' && timeEntries.length > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 text-xs rounded-full bg-muted text-muted-foreground font-bold">
                  {timeEntries.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader" className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* ════════════ OVERVIEW ════════════ */}
              {activeTab === 'overview' && (
                <div className="space-y-5">
                  {/* Client card */}
                  {c.client && (
                    <div className="flex items-center gap-4 p-4 bg-background border border-border rounded-xl">
                      <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{c.client?.full_name || 'Client'}</p>
                        <p className="text-sm text-muted-foreground">{c.client?.email}</p>
                      </div>
                      <div className="ml-auto">
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Client</span>
                      </div>
                    </div>
                  )}

                  {/* Details grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {c.contract_type === 'hourly' && (
                      <>
                        <InfoRow icon="DollarSign" label="Hourly Rate"  value={`$${c.hourly_rate?.toFixed(2)}/hr`} />
                        <InfoRow icon="Target"     label="Budget Cap"   value={c.budget_cap ? `$${c.budget_cap?.toFixed(2)}` : 'No cap'} />
                      </>
                    )}
                    {c.contract_type === 'milestone' && (
                      <>
                        <InfoRow icon="DollarSign" label="Total Value"     value={`$${c.total_contract_value?.toFixed(2)}`} />
                        <InfoRow icon="CheckSquare" label="Milestones"     value={`${approvedMs} / ${milestones.length} approved`} />
                      </>
                    )}
                    {c.deadline && (
                      <InfoRow icon="Calendar"   label="Deadline"      value={new Date(c.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
                    )}
                    {c.start_date && (
                      <InfoRow icon="PlayCircle" label="Start Date"    value={new Date(c.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
                    )}
                    <InfoRow icon="Hash"        label="Contract #"    value={c.contract_number} />
                    <InfoRow icon="CreditCard"  label="Escrow Status" value={c.escrow_status || 'none'} />
                  </div>

                  {/* Linked case */}
                  {c.case_id && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Linked Case</h4>
                      <button
                        onClick={() => { onClose(); navigate(`/report-viewer/${c.case_id}`); }}
                        className="w-full flex items-center gap-4 p-4 bg-background border border-border rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-colors text-left group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="FileText" size={18} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">
                            {c.case?.case_number ? `Case #${c.case.case_number}` : 'Linked Case'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {c.case?.property_address || c.case?.allegation_type || 'Click to view report'}
                          </p>
                        </div>
                        <Icon name="ExternalLink" size={16} className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                      </button>
                    </div>
                  )}

                  {/* Description */}
                  {c.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Description</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-4">{c.description}</p>
                    </div>
                  )}

                  {/* Deliverables */}
                  {c.deliverables && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Deliverables</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-4 whitespace-pre-wrap">{c.deliverables}</p>
                    </div>
                  )}

                  {/* Timeline */}
                  {(c.created_at || c.accepted_at || c.completed_at) && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-3">Timeline</h4>
                      <div className="space-y-2">
                        {[
                          { label: 'Created',   ts: c.created_at,   icon: 'FileText',     color: 'text-muted-foreground' },
                          { label: 'Accepted',  ts: c.accepted_at,  icon: 'CheckCircle',  color: 'text-success' },
                          { label: 'Started',   ts: c.start_date,   icon: 'Play',         color: 'text-primary' },
                          { label: 'Completed', ts: c.completed_at, icon: 'Trophy',       color: 'text-success' },
                          { label: 'Cancelled', ts: c.cancelled_at, icon: 'XCircle',      color: 'text-destructive' },
                        ].filter(e => e.ts).map((ev) => (
                          <div key={ev.label} className="flex items-center gap-3 text-sm">
                            <Icon name={ev.icon} size={15} className={ev.color} />
                            <span className="text-muted-foreground w-20">{ev.label}</span>
                            <span className="text-foreground font-medium">
                              {new Date(ev.ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {canAct && (
                    <div className="pt-3 border-t border-border flex flex-wrap gap-3">
                      {c.status === 'offered' && (
                        <Button
                          onClick={() => handleAction(acceptContract, 'Accept', [c.id])}
                          disabled={!!actionLoading}
                          iconName="CheckCircle"
                        >
                          {actionLoading === 'Accept' ? 'Accepting...' : 'Accept Contract'}
                        </Button>
                      )}
                      {c.status === 'accepted' && (
                        <Button
                          onClick={() => handleAction(startContract, 'Start', [c.id])}
                          disabled={!!actionLoading}
                          iconName="Play"
                        >
                          {actionLoading === 'Start' ? 'Starting...' : 'Start Work'}
                        </Button>
                      )}
                      {c.status === 'in_progress' && (
                        <Button
                          onClick={() => handleAction(submitContractForReview, 'Submit', [c.id])}
                          disabled={!!actionLoading}
                          iconName="Send"
                        >
                          {actionLoading === 'Submit' ? 'Submitting...' : 'Submit for Review'}
                        </Button>
                      )}
                      {c.status === 'pending_review' && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning font-medium w-full">
                          <Icon name="Clock" size={15} className="flex-shrink-0" />
                          Waiting for client to review &amp; approve
                        </div>
                      )}
                      {!showCancelConfirm ? (
                        <Button
                          variant="outline"
                          className="text-destructive border-destructive/30 hover:bg-destructive/10"
                          onClick={() => setShowCancelConfirm(true)}
                          iconName="XCircle"
                        >
                          Cancel Contract
                        </Button>
                      ) : (
                        <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20 space-y-2 w-full">
                          <div className="flex items-center gap-2">
                            <Icon name="AlertTriangle" size={16} className="text-destructive flex-shrink-0" />
                            <p className="text-sm text-destructive font-medium">Are you sure you want to cancel this contract?</p>
                          </div>
                          <textarea
                            rows={2}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Reason for cancellation (required)…"
                            className="w-full px-3 py-2 text-sm bg-background border border-destructive/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-destructive/40 resize-none"
                            maxLength={500}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive border-destructive/30"
                              onClick={() => handleCancelContract()}
                              disabled={!!actionLoading || !cancelReason.trim()}
                            >
                              {actionLoading === 'Cancel' ? 'Cancelling...' : 'Yes, Cancel'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setShowCancelConfirm(false); setCancelReason(''); }}
                            >
                              No
                            </Button>
                          </div>
                        </div>
                      )}
                      {!['completed','cancelled','disputed'].includes(c.status) && !showDisputeConfirm && (
                        <Button
                          variant="outline"
                          className="text-warning border-warning/30 hover:bg-warning/10"
                          onClick={() => setShowDisputeConfirm(true)}
                          iconName="AlertTriangle"
                        >
                          File a Dispute
                        </Button>
                      )}
                      {!['completed','cancelled','disputed'].includes(c.status) && showDisputeConfirm && (
                        <div className="p-3 bg-warning/10 rounded-lg border border-warning/20 space-y-2 w-full">
                          <div className="flex items-center gap-2">
                            <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0" />
                            <p className="text-sm text-warning font-medium">File a dispute? Admins will be notified immediately.</p>
                          </div>
                          <textarea
                            rows={3}
                            value={disputeReason}
                            onChange={(e) => setDisputeReason(e.target.value)}
                            placeholder="Describe your dispute in detail (required)…"
                            className="w-full px-3 py-2 text-sm bg-background border border-warning/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-warning/40 resize-none"
                            maxLength={1000}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-warning border-warning/30"
                              onClick={async () => {
                                if (!disputeReason.trim()) return toast.error('Please describe the dispute');
                                setActionLoading('Dispute');
                                try {
                                  const r = await disputeContract(c.id, disputeReason.trim());
                                  if (!r.success) throw new Error(r.error);
                                  toast.success('Dispute filed — our team has been notified');
                                  setShowDisputeConfirm(false);
                                  setDisputeReason('');
                                  onRefresh?.();
                                } catch (err) {
                                  toast.error(err?.message || 'Failed to file dispute');
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                              disabled={!!actionLoading || !disputeReason.trim()}
                            >
                              {actionLoading === 'Dispute' ? 'Filing...' : 'Submit Dispute'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setShowDisputeConfirm(false); setDisputeReason(''); }}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ════════════ MILESTONES ════════════ */}
              {activeTab === 'milestones' && (
                <div className="space-y-5">
                  {milestones.length === 0 ? (
                    <EmptyState icon="CheckSquare" message="No milestones defined for this contract." />
                  ) : (
                    <>
                      {/* Progress bar */}
                      <div className="bg-background border border-border rounded-xl p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-foreground">Overall Progress</span>
                          <span className="text-sm font-bold text-primary">{approvedMs}/{milestones.length} approved</span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700"
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {[
                              { label: 'Pending',   color: 'bg-muted',           count: milestones.filter(m => m.status === 'pending').length },
                              { label: 'Submitted', color: 'bg-warning',         count: milestones.filter(m => m.status === 'submitted').length },
                              { label: 'Approved',  color: 'bg-success',         count: approvedMs },
                              { label: 'Rejected',  color: 'bg-destructive',     count: milestones.filter(m => m.status === 'rejected').length },
                            ].filter(s => s.count > 0).map((s) => (
                              <span key={s.label} className="flex items-center gap-1">
                                <span className={`w-2 h-2 rounded-full ${s.color}`} />
                                {s.count} {s.label}
                              </span>
                            ))}
                          </div>
                          <span className="text-sm font-bold text-primary">{progressPct}%</span>
                        </div>
                      </div>

                      {/* Milestone cards */}
                      <div className="space-y-3">
                        {milestones.map((ms, i) => (
                          <div key={ms.id} className={`bg-background border rounded-xl p-4 transition-colors ${
                            ms.status === 'approved'  ? 'border-success/30 bg-success/5'  :
                            ms.status === 'rejected'  ? 'border-destructive/30'            :
                            ms.status === 'submitted' ? 'border-warning/30 bg-warning/5'  :
                            'border-border'
                          }`}>
                            <div className="flex items-start gap-4">
                              {/* Step indicator */}
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                                ms.status === 'approved'  ? 'bg-success text-white'        :
                                ms.status === 'submitted' ? 'bg-warning text-white'        :
                                ms.status === 'rejected'  ? 'bg-destructive text-white'    :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {ms.status === 'approved' ? <Icon name="Check" size={16} /> : i + 1}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
                                  <p className="font-semibold text-foreground">{ms.title}</p>
                                  <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium flex-shrink-0 ${MILESTONE_CHIP[ms.status] || 'bg-muted text-muted-foreground'}`}>
                                    {ms.status?.replace('_', ' ')}
                                  </span>
                                </div>

                                {ms.description && (
                                  <p className="text-sm text-muted-foreground mb-2">{ms.description}</p>
                                )}

                                <div className="flex items-center flex-wrap gap-4 text-sm">
                                  <span className="font-bold text-foreground text-base">${ms.amount?.toFixed(2)}</span>
                                  {ms.due_date && (
                                    <span className={`flex items-center gap-1 text-xs ${
                                      new Date(ms.due_date) < new Date() && ms.status !== 'approved' ? 'text-destructive font-medium' : 'text-muted-foreground'
                                    }`}>
                                      <Icon name="Calendar" size={12} />
                                      Due {new Date(ms.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                  )}
                                  {ms.submitted_at && (
                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Icon name="Send" size={12} />
                                      Submitted {new Date(ms.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  )}
                                  {ms.approved_at && (
                                    <span className="flex items-center gap-1 text-xs text-success">
                                      <Icon name="CheckCircle" size={12} />
                                      Approved {new Date(ms.approved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  )}
                                </div>

                                {ms.rejection_reason && (
                                  <div className="mt-2.5 px-3 py-2 bg-destructive/10 rounded-lg border border-destructive/20">
                                    <p className="text-xs text-destructive font-medium">
                                      <Icon name="AlertCircle" size={12} className="inline mr-1" />
                                      Rejection: {ms.rejection_reason}
                                    </p>
                                  </div>
                                )}

                                {['pending', 'in_progress', 'rejected'].includes(ms.status) && c.status === 'in_progress' && (
                                  <div className="mt-3">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleSubmitMilestone(ms.id)}
                                      disabled={actionLoading === `ms-${ms.id}`}
                                      iconName="Send"
                                      iconPosition="left"
                                    >
                                      {actionLoading === `ms-${ms.id}` ? 'Submitting...' : ms.status === 'rejected' ? 'Resubmit for Review' : 'Submit for Review'}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ════════════ TIMESHEET ════════════ */}
              {activeTab === 'timesheet' && (
                <div className="space-y-5">
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Total Hours',  value: `${totalHours}h`,               icon: 'Clock',        color: 'text-primary',        bg: 'bg-primary/10'     },
                      { label: 'Total Billed', value: `$${totalBill.toFixed(2)}`,      icon: 'DollarSign',   color: 'text-foreground',     bg: 'bg-muted'          },
                      { label: 'Approved',     value: `$${approvedBill.toFixed(2)}`,   icon: 'CheckCircle',  color: 'text-success',        bg: 'bg-success/10'     },
                      { label: 'Pending',      value: `$${pendingBill.toFixed(2)}`,    icon: 'Clock',        color: 'text-warning',        bg: 'bg-warning/10'     },
                    ].map((s) => (
                      <div key={s.label} className="bg-background border border-border rounded-xl p-4 flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon name={s.icon} size={16} className={s.color} />
                        </div>
                        <div>
                          <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                          <p className="text-xs text-muted-foreground">{s.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Log time form */}
                  {['accepted', 'in_progress'].includes(c.status) && (
                    <form onSubmit={handleLogTime} className="bg-background border border-border rounded-xl p-5 space-y-4">
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Icon name="PlusCircle" size={16} className="text-primary" />
                        Log Time Entry
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Date <span className="text-destructive">*</span></label>
                          <Input
                            type="date"
                            value={entry.workDate}
                            onChange={e => setEntry(p => ({ ...p, workDate: e.target.value }))}
                            max={new Date().toISOString().split('T')[0]}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Hours <span className="text-destructive">*</span></label>
                          <Input
                            type="number"
                            min="0.5"
                            max="24"
                            step="0.5"
                            placeholder="e.g. 2.5"
                            value={entry.hours}
                            onChange={e => setEntry(p => ({ ...p, hours: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Rate</label>
                          <Input
                            value={`$${c.hourly_rate?.toFixed(2)}/hr  →  $${((parseFloat(entry.hours) || 0) * c.hourly_rate).toFixed(2)}`}
                            readOnly
                            className="bg-muted cursor-default text-muted-foreground"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Description <span className="text-destructive">*</span></label>
                        <textarea
                          className="w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none"
                          rows={2}
                          placeholder="Describe the work done in this session..."
                          value={entry.description}
                          onChange={e => setEntry(p => ({ ...p, description: e.target.value }))}
                          required
                        />
                      </div>
                      <Button type="submit" size="sm" disabled={submittingEntry} iconName={submittingEntry ? 'Loader' : 'Plus'} iconPosition="left">
                        {submittingEntry ? 'Logging...' : 'Log Time'}
                      </Button>
                    </form>
                  )}

                  {/* Entries table */}
                  {timeEntries.length === 0 ? (
                    <EmptyState icon="Clock" message="No time entries yet. Log your first session above." />
                  ) : (
                    <div className="bg-background border border-border rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 border-b border-border">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Date</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Description</th>
                              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Hours</th>
                              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {timeEntries.map((e) => (
                              <tr key={e.id} className="hover:bg-muted/30 transition-colors">
                                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                                  {new Date(e.work_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </td>
                                <td className="px-4 py-3 text-foreground max-w-[200px] truncate" title={e.description}>
                                  {e.description}
                                </td>
                                <td className="px-4 py-3 text-right text-foreground font-medium">
                                  {(e.minutes / 60).toFixed(1)}h
                                </td>
                                <td className="px-4 py-3 text-right font-bold text-foreground">
                                  ${e.billable_amount?.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ════════════ PAYMENTS ════════════ */}
              {activeTab === 'payments' && (
                <div className="space-y-5">

                  {/* Summary */}
                  <div className="bg-background border border-border rounded-xl p-5">
                    <h4 className="text-sm font-semibold text-foreground mb-4">Payment Breakdown</h4>
                    <div className="divide-y divide-border/50">
                      {c.contract_type === 'hourly' && (
                        <>
                          <PayRow label="Hourly Rate"     value={`$${c.hourly_rate?.toFixed(2)}/hr`} />
                          <PayRow label="Budget Cap"      value={c.budget_cap ? `$${c.budget_cap?.toFixed(2)}` : 'None'} />
                          <PayRow label="Hours Logged"    value={`${totalHours}h`} />
                          <PayRow label="Total Billed"    value={`$${totalBill.toFixed(2)}`} />
                          <PayRow label="Approved Amount" value={`$${approvedBill.toFixed(2)}`} isHighlit />
                        </>
                      )}
                      {c.contract_type === 'milestone' && (
                        <>
                          <PayRow label="Total Contract Value" value={`$${c.total_contract_value?.toFixed(2)}`} />
                          <PayRow label="Milestones"           value={`${approvedMs} / ${milestones.length} approved`} />
                          <PayRow label="Approved Value"       value={`$${approvedMsValue.toFixed(2)}`} isHighlit />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Escrow card */}
                  <div className={`rounded-xl border p-5 ${
                    c.escrow_status === 'released' ? 'border-success/40 bg-success/5'  :
                    c.escrow_status === 'held'     ? 'border-warning/40 bg-warning/5'  :
                    c.escrow_status === 'refunded' ? 'border-muted bg-muted/30'        :
                    'border-border bg-background'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        c.escrow_status === 'released' ? 'bg-success/15' :
                        c.escrow_status === 'held'     ? 'bg-warning/15' :
                        'bg-muted'
                      }`}>
                        <Icon
                          name={
                            c.escrow_status === 'released' ? 'Unlock'       :
                            c.escrow_status === 'held'     ? 'Lock'         :
                            c.escrow_status === 'refunded' ? 'CornerUpLeft' :
                            'CreditCard'
                          }
                          size={18}
                          className={
                            c.escrow_status === 'released' ? 'text-success'           :
                            c.escrow_status === 'held'     ? 'text-warning'           :
                            'text-muted-foreground'
                          }
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground capitalize">
                          Escrow: {c.escrow_status || 'Not configured'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {c.escrow_status === 'released' && 'Funds have been released and are being transferred.'}
                          {c.escrow_status === 'held'     && 'Client funds are held in escrow and will be released upon completion.'}
                          {c.escrow_status === 'refunded' && 'Escrow has been refunded to the client.'}
                          {!c.escrow_status || c.escrow_status === 'none' ? 'No escrow configured for this contract.' : ''}
                        </p>
                        {c.escrow_amount > 0 && (
                          <p className="mt-2 text-sm">
                            Held amount: <span className="font-bold text-foreground">${c.escrow_amount?.toFixed(2)}</span>
                          </p>
                        )}
                        {c.stripe_payment_intent_id && (
                          <p className="mt-1.5 text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded w-fit">
                            {c.stripe_payment_intent_id}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Milestone payment breakdown */}
                  {c.contract_type === 'milestone' && milestones.length > 0 && (
                    <div className="bg-background border border-border rounded-xl overflow-hidden">
                      <div className="px-5 py-4 border-b border-border">
                        <h4 className="text-sm font-semibold text-foreground">Milestone Payments</h4>
                      </div>
                      <div className="divide-y divide-border">
                        {milestones.map((ms) => (
                          <div key={ms.id} className="flex items-center justify-between px-5 py-3 gap-4">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-foreground truncate">{ms.title}</p>
                              {ms.due_date && (
                                <p className="text-xs text-muted-foreground">
                                  Due {new Date(ms.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${MILESTONE_CHIP[ms.status] || 'bg-muted text-muted-foreground'}`}>
                                {ms.status?.replace('_', ' ')}
                              </span>
                              <p className="text-sm font-bold text-foreground w-20 text-right">${ms.amount?.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                        <div className="flex items-center justify-between px-5 py-3 bg-muted/30">
                          <p className="text-sm font-semibold text-foreground">Total</p>
                          <p className="text-sm font-bold text-foreground">
                            ${milestones.reduce((s, m) => s + (m.amount || 0), 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractDetailModal;
